import { ConnectionType } from 'modules/core/supabase/tables/match-chronology';
import supabase from 'modules/core/supabase/supabase';
import { selectBroadCastState } from 'modules/overlay-v2/store/overlay/selectors';
import { useEffect, useRef, useState } from 'react';
import { time } from 'shared/constants/time';
import { useAppSelector } from 'store/hooks';

/** Пауза между дозапросами журнала, пока realtime-канал лежит. */
export const CHRONOLOGY_FALLBACK_POLL_INTERVAL = time.second * 3;

/**
 * Резервный опрос журнала матча: пока realtime-канал не `SUBSCRIBED`, состояние
 * догоняется запросами раз в {@link CHRONOLOGY_FALLBACK_POLL_INTERVAL}.
 *
 * Это единственный механизм восстановления в эфире — автоповтор подготовки после выхода
 * в эфир заглушен намеренно (`use-overlay-readiness.ts`), поэтому опрос обязан работать
 * и на оверлее, и на панели управления. Вызывается из `use-init-subscribe-overlay-v2`,
 * а не из джойстика: джойстик монтируется только под фичефлагом `OBS_INTEGRATION`.
 *
 * IMPORTANT: `matchId` берётся из стора трансляции, а не из `useParams()`. В роутах
 * оверлея (`Pages.OVERLAY_V2`) сегмента `matchId` нет, и прежний опрос из джойстика
 * уходил в базу как `matchId=eq.NaN` — то есть не работал вообще (IMGM-1435).
 */
function useChronologyFallbackPoll() {
  const { matchId } = useAppSelector(selectBroadCastState);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionType>(supabase.matchChronology.connectionStatus.value);
  /** Опрос уже что-то запрашивал — значит про возврат связи есть смысл писать в журнал. */
  const didPollRef = useRef(false);

  useEffect(() => {
    const unsubscribe = supabase.matchChronology.connectionStatus.subscribe(setConnectionStatus);
    // Статус мог смениться между первым рендером и подпиской.
    setConnectionStatus(supabase.matchChronology.connectionStatus.value);
    return unsubscribe;
  }, []);

  const isChannelAlive = connectionStatus === 'SUBSCRIBED';

  useEffect(() => {
    if (!isChannelAlive) return;
    if (!didPollRef.current) return;
    didPollRef.current = false;
    supabase.overlayLogs.createInfo({
      message: 'Соединение с джойстиком установленно. Режим запроса данных выключен.',
    });
  }, [isChannelAlive]);

  useEffect(() => {
    if (matchId === undefined) return;
    if (isChannelAlive) return;

    let cancelled = false;

    const poll = async () => {
      if (!didPollRef.current) {
        didPollRef.current = true;
        supabase.overlayLogs.createInfo({
          message: 'Нет соединения с джойстиком. Включен режим запроса данных.',
        });
      }

      try {
        const row = await supabase.matchChronology.getRow(matchId);
        if (cancelled || !row) return;
        // Метка обязана быть уникальной: `handleRecordUpdated` отбрасывает повтор по её
        // значению, и `toDateString()` (как было в джойстике) заглушил бы всё после
        // первого опроса за сутки.
        supabase.matchChronology.handleRecordUpdated({ new: row, commit_timestamp: new Date().toISOString() });
      } catch (error) {
        console.error({ event: 'chronology fallback poll failed', error });
      }
    };

    // Ровно один интервал на связку «матч + лежащий канал»; повторные обрывы не копят таймеры.
    const interval = setInterval(poll, CHRONOLOGY_FALLBACK_POLL_INTERVAL);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isChannelAlive, matchId]);
}

export default useChronologyFallbackPoll;
